import React, { useRef } from "react"

import { Form, Formik } from "formik"
import * as Yup from "yup"

import { XMarkIcon } from "@heroicons/react/24/outline"
import { PaperAirplaneIcon, PaperClipIcon } from "@heroicons/react/24/solid"

import { Failures, TextAreaInput, handleFailure } from "~tailwindui/Form"

import classNames from "src/classNames"
import readFile from "src/readFile"

import { gql, useClient, useMutation } from "hooks/urql"

export type SendMessageProps = {
  threadId: string
}

const ValidationSchema = Yup.object().shape({
  content: Yup.string().required("Message body required"),
})

const SendMessage: React.FC<SendMessageProps> = ({ threadId }) => {
  const client = useClient()
  const formState = { content: "", threadId }
  const fileField = useRef<any>()
  const [, runMutation]: any[] = useMutation(createMessageMutation, client)

  const handleSubmit = (values, actions) => {
    const params = { ...values }
    readFile(values.attachment)
      .then(fileResult => {
        params.attachment = fileResult
        runMutation(params)
          .then(result => {
            const { failures } = result.data.createMessage

            if (failures.length > 0) {
              handleFailure(actions, failures)
            } else {
              actions.resetForm()
              if (fileField.current) fileField.current.value = ""
              actions.setSubmitting(false)
            }
          })
          .catch(() => handleFailure(actions))
      })
      .catch(() => handleFailure(actions))
  }

  return (
    <Formik
      initialValues={formState}
      onSubmit={handleSubmit}
      validationSchema={ValidationSchema}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      {({ isSubmitting, setFieldValue, values }) => (
        <Form>
          <div className="relative flex gap-2">
            <TextAreaInput
              label=""
              name="content"
              placeholder="Send a message"
              rows={3}
              autoFocus
            />

            <div className="mb-3 flex flex-col justify-end gap-1">
              <button
                type="button"
                onClick={() =>
                  document.getElementById("attachment-file-input")?.click()
                }
                className={classNames(
                  "rounded-lg p-2",
                  fileField.current?.value
                    ? "cursor-not-allowed bg-gray-200 text-gray-500"
                    : "cursor-pointer bg-green-400 text-white hover:bg-green-500 focus-visible:bg-green-500"
                )}
                disabled={!!fileField.current?.value}
              >
                <PaperClipIcon className="h-6 w-6" />
              </button>
              <input
                type="file"
                id="attachment-file-input"
                ref={fileField}
                className="hidden"
                onChange={e =>
                  setFieldValue("attachment", e.currentTarget.files?.[0])
                }
              />
              <button
                type="submit"
                className={classNames(
                  "cursor-pointer rounded-lg p-2",
                  isSubmitting
                    ? "bg-sky-200 text-sky-600"
                    : "bg-sky-500 text-white hover:bg-sky-600 focus-visible:bg-sky-600"
                )}
              >
                <PaperAirplaneIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div
            className={classNames(
              "mt-2 flex justify-between gap-2 rounded-xl bg-gray-300 px-4 py-2",
              !values.attachment && "hidden"
            )}
          >
            <span>{values.attachment?.name}</span>
            <button
              type="button"
              onClick={() => {
                setFieldValue("attachment", null)
                if (fileField.current) fileField.current.value = ""
              }}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <Failures />
        </Form>
      )}
    </Formik>
  )
}

const createMessageMutation = gql`
  mutation CreateMessage(
    $threadId: ID!
    $content: String!
    $attachment: FileInputObject
  ) {
    createMessage(
      messageThreadId: $threadId
      content: $content
      attachment: $attachment
    ) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`

export default SendMessage
